import { effect } from '@crown/signal/effect';
import { type MessageArgs, parseMessageArgs } from '../types';
import { translator } from './state';

/**
 * Elements opt in with data attributes; nothing else on the page is touched.
 *
 *   <h1 data-i18n="auth.signIn.title"></h1>
 *   <input data-i18n-attr="placeholder:auth.email.hint;aria-label:auth.email.label">
 *   <p data-i18n="common.count.records" data-i18n-params='{"count":3}'></p>
 */
const TEXT_ATTR = 'data-i18n';
const ATTRS_ATTR = 'data-i18n-attr';
const PARAMS_ATTR = 'data-i18n-params';
const SELECTOR = `[${TEXT_ATTR}],[${ATTRS_ATTR}]`;

const warnedParams = new WeakSet<Element>();

/**
 * What the page was authored with, kept the first time an element is rendered.
 * Markup carries its English — `<span data-i18n="nav.overview">Overview</span>`
 * — so a key no catalogue defines leaves that text alone rather than writing
 * the key over it.
 */
interface AuthoredText {
  /** The element's own text, when it has any. */
  readonly text: string | undefined;
  /** Each attribute as the page was served, by attribute name. */
  readonly attrs: Map<string, string>;
}

const authored = new WeakMap<Element, AuthoredText>();

function authoredOf(element: Element): AuthoredText {
  const seen = authored.get(element);
  if (seen) return seen;
  const attrs = new Map<string, string>();
  for (const attribute of element.attributes) {
    if (attribute.name.startsWith('data-i18n')) continue;
    attrs.set(attribute.name, attribute.value);
  }
  // Blank markup has nothing to fall back to, and there the key is the most
  // useful thing to show: it names what is missing instead of rendering a gap.
  const text = element.textContent?.trim() ? element.textContent : undefined;
  const record = { text, attrs };
  authored.set(element, record);
  return record;
}

function paramsOf(element: Element): MessageArgs | undefined {
  const raw = element.getAttribute(PARAMS_ATTR);
  if (!raw) return undefined;
  try {
    return parseMessageArgs(JSON.parse(raw));
  } catch {
    // fall through to the warning
  }
  if (!warnedParams.has(element)) {
    warnedParams.add(element);
    console.warn(`[i18n] ${PARAMS_ATTR} is not a JSON object`, element);
  }
  return undefined;
}

export function renderI18n(element: Element): void {
  const current = translator.get();
  const args = paramsOf(element);
  const source = authoredOf(element);
  const textKey = element.getAttribute(TEXT_ATTR);
  if (textKey)
    element.textContent = current.translate(textKey, args, source.text);
  const attrs = element.getAttribute(ATTRS_ATTR);
  if (attrs) {
    for (const pair of attrs.split(';')) {
      const colon = pair.indexOf(':');
      if (colon === -1) continue;
      const name = pair.slice(0, colon).trim();
      const key = pair.slice(colon + 1).trim();
      if (!name || !key) continue;
      element.setAttribute(
        name,
        current.translate(key, args, source.attrs.get(name)),
      );
    }
  }
}

function collect(node: Node, into: Set<Element>): void {
  if (!(node instanceof Element)) return;
  if (node.matches(SELECTOR)) into.add(node);
  for (const child of node.querySelectorAll(SELECTOR)) into.add(child);
}

/**
 * Renders every bound element under `root` now, keeps rendering ones that
 * arrive later, and re-renders all of them when the translator changes.
 * Returns a function that stops all three.
 */
export function bindI18n(root: ParentNode & Node): () => void {
  const bound = new Set<Element>();
  collect(root, bound);

  const observer = new MutationObserver((mutations) => {
    const added = new Set<Element>();
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if (mutation.target instanceof Element) {
          if (mutation.target.matches(SELECTOR)) added.add(mutation.target);
          else bound.delete(mutation.target);
        }
        continue;
      }
      for (const node of mutation.addedNodes) collect(node, added);
      for (const node of mutation.removedNodes) {
        const gone = new Set<Element>();
        collect(node, gone);
        for (const element of gone) bound.delete(element);
      }
    }
    for (const element of added) {
      bound.add(element);
      renderI18n(element);
    }
  });
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [TEXT_ATTR, ATTRS_ATTR, PARAMS_ATTR],
  });

  const stop = effect(() => {
    translator.get();
    for (const element of bound) {
      if (element.isConnected) renderI18n(element);
      else bound.delete(element);
    }
  });

  return () => {
    observer.disconnect();
    stop();
    bound.clear();
  };
}
